import React, { useRef } from 'react';
import { Map, TileLayer, Marker, Popup, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/RouteMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BASE_LOCATION = {
  name: 'Almacén Central',
  lat: parseFloat(process.env.REACT_APP_BASE_LAT) || -12.118599,
  lng: parseFloat(process.env.REACT_APP_BASE_LNG) || -77.018711,
};

const SPEED_KMH = 25;
const STOP_MIN  = 5;

// ─── helpers ────────────────────────────────────────────────────────────────

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function nearestNeighborSort(items, baseLat, baseLng) {
  if (items.length <= 1) return [...items];
  const unvisited = [...items];
  const sorted = [];
  let curLat = baseLat;
  let curLng = baseLng;
  while (unvisited.length > 0) {
    let nearestIdx = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < unvisited.length; i++) {
      const d = haversine(curLat, curLng, parseFloat(unvisited[i].latitude), parseFloat(unvisited[i].longitude));
      if (d < nearestDist) { nearestDist = d; nearestIdx = i; }
    }
    const next = unvisited.splice(nearestIdx, 1)[0];
    sorted.push(next);
    curLat = parseFloat(next.latitude);
    curLng = parseFloat(next.longitude);
  }
  return sorted;
}

// windowStart: "16:00:00" o "16:00"
function buildTSPRoute(sortedItems, windowStart) {
  let startMin = 0;
  if (windowStart) {
    const parts = windowStart.split(':');
    startMin = parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }

  let cumMin = 0;
  let prevLat = BASE_LOCATION.lat;
  let prevLng = BASE_LOCATION.lng;

  return sortedItems.map(function(item, i) {
    const lat = parseFloat(item.latitude);
    const lng = parseFloat(item.longitude);
    const dist = haversine(prevLat, prevLng, lat, lng);
    const travelMin = Math.ceil(dist / SPEED_KMH * 60);
    cumMin += travelMin;

    const totalMin = startMin + cumMin;
    const h = Math.floor(totalMin / 60) % 24;
    const m = totalMin % 60;
    const eta = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');

    cumMin += STOP_MIN;
    prevLat = lat;
    prevLng = lng;

    return Object.assign({}, item, {
      tspPosition: i + 1,
      tspETA: eta,
      tspDist: dist,
      tspTravel: travelMin,
    });
  });
}

function getStopColor(item) {
  if (item.priority) return '#7c3aed';
  if (item.status === 'CONFIRMED') return '#2563eb';
  if (item.status === 'PENDING') return '#f59e0b';
  return '#64748b';
}

function createStopIcon(position, item) {
  const color = getStopColor(item);
  const label = item.priority ? '★' : String(position);
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-pin" style="background-color:${color};">
        <span class="marker-number" style="font-size:${label.length > 1 ? '11px' : '14px'}">${label}</span>
      </div>
      <div class="marker-pulse" style="background-color:${color}55;"></div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
}

const baseIcon = L.divIcon({
  className: 'custom-marker-start',
  html: `
    <div class="marker-pin-start"><span class="marker-icon">🏠</span></div>
    <div class="marker-pulse-start"></div>
  `,
  iconSize: [45, 45],
  iconAnchor: [22, 45],
  popupAnchor: [0, -45],
});

// ─── componente ──────────────────────────────────────────────────────────────

const QueuePreviewMap = function(props) {
  const queue        = props.queue        || [];
  const windowLabel  = props.windowLabel  || '';
  const windowStart  = props.windowStart  || null;
  const routeCapacity = props.routeCapacity || null;
  const mapRef = useRef(null);

  const validItems = queue.filter(function(item) {
    return item.latitude && item.longitude;
  });

  if (validItems.length === 0) {
    return (
      <div className="route-map-container">
        <div className="no-route-message">
          <p>📭 No hay pedidos con coordenadas en esta ventana</p>
          <p className="text-muted">Los pedidos aparecerán aquí cuando tengan dirección geocodificada</p>
        </div>
      </div>
    );
  }

  // Orden real de conducción (nearest-neighbor desde la base)
  const tspItems = buildTSPRoute(
    nearestNeighborSort(validItems, BASE_LOCATION.lat, BASE_LOCATION.lng),
    windowStart
  );

  // Centro del mapa
  const allLats = [BASE_LOCATION.lat].concat(tspItems.map(function(i) { return parseFloat(i.latitude); }));
  const allLngs = [BASE_LOCATION.lng].concat(tspItems.map(function(i) { return parseFloat(i.longitude); }));
  const center = [
    allLats.reduce(function(s, v) { return s + v; }, 0) / allLats.length,
    allLngs.reduce(function(s, v) { return s + v; }, 0) / allLngs.length,
  ];

  // Polilínea: base → paradas en orden TSP
  const routeLine = [[BASE_LOCATION.lat, BASE_LOCATION.lng]].concat(
    tspItems.map(function(item) {
      return [parseFloat(item.latitude), parseFloat(item.longitude)];
    })
  );

  // Retorno a base (punteado)
  const lastItem = tspItems[tspItems.length - 1];
  const returnLine = [
    [parseFloat(lastItem.latitude), parseFloat(lastItem.longitude)],
    [BASE_LOCATION.lat, BASE_LOCATION.lng],
  ];

  // Tiempo total TSP (calculado en frontend, igual o menor que routeCapacity.totalTime)
  const tspTotalMin = tspItems.reduce(function(acc, item) {
    return acc + item.tspTravel + STOP_MIN;
  }, 0) + Math.ceil(haversine(
    parseFloat(lastItem.latitude), parseFloat(lastItem.longitude),
    BASE_LOCATION.lat, BASE_LOCATION.lng
  ) / SPEED_KMH * 60);

  const maxTime = routeCapacity ? routeCapacity.maxTime : 90;
  const fits = tspTotalMin <= maxTime;
  const lastETA = tspItems[tspItems.length - 1].tspETA;

  return (
    <div className="route-map-container">
      <div className="map-header">
        <h3>🗺️ Orden de conducción — Ventana {windowLabel}</h3>
        <div className="map-stats">
          <span className="map-stat">📦 {tspItems.length} pedidos</span>
          <span className={'map-stat' + (!fits ? ' traffic-real' : '')}>
            ⏱️ {tspTotalMin} / {maxTime} min {!fits ? '⚠️ EXCEDIDO' : ''}
          </span>
          {lastETA && (
            <span className="map-stat">🕐 Última entrega ~{lastETA}</span>
          )}
          <span className="map-stat" style={{ background: 'rgba(124,58,237,0.3)' }}>
            🟣 Prioridad &nbsp; 🔵 Confirmado &nbsp; 🟡 Pendiente
          </span>
        </div>
      </div>

      <Map
        center={center}
        zoom={13}
        style={{ height: '520px', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Ruta principal: base → paradas en orden TSP */}
        <Polyline positions={routeLine} color="#2563eb" weight={3} opacity={0.75} />

        {/* Retorno a base (punteado) */}
        <Polyline
          positions={returnLine}
          color="#94a3b8"
          weight={2}
          opacity={0.6}
          dashArray="8 6"
        />

        {/* Marcador base */}
        <Marker position={[BASE_LOCATION.lat, BASE_LOCATION.lng]} icon={baseIcon}>
          <Popup maxWidth={220}>
            <div className="stop-popup">
              <div className="popup-header"><strong>🏠 {BASE_LOCATION.name}</strong></div>
              <div className="popup-content"><p>Punto de salida y retorno</p></div>
            </div>
          </Popup>
          <Tooltip direction="top" offset={[0, -20]} opacity={0.9}>
            <strong>Base — {BASE_LOCATION.name}</strong>
          </Tooltip>
        </Marker>

        {/* Marcadores en orden TSP */}
        {tspItems.map(function(item) {
          const pos = [parseFloat(item.latitude), parseFloat(item.longitude)];
          const color = getStopColor(item);

          return (
            <Marker
              key={item.purchaseId}
              position={pos}
              icon={createStopIcon(item.tspPosition, item)}
            >
              <Popup maxWidth={260}>
                <div className="stop-popup">
                  <div className="popup-header">
                    <strong>#{item.tspPosition} — {item.customerName || 'Pedido #' + item.purchaseId}</strong>
                  </div>
                  <div className="popup-content">
                    {item.district && <p><strong>Distrito:</strong> {item.district}</p>}
                    <p><strong>Distancia desde anterior:</strong> {item.tspDist.toFixed(2)} km</p>
                    <p><strong>Tiempo de viaje:</strong> {item.tspTravel} min</p>
                    <p><strong>Hora estimada de llegada:</strong> {item.tspETA}</p>
                    <hr />
                    <p>
                      <strong>Estado:</strong>{' '}
                      <span style={{ color: color, fontWeight: 600 }}>
                        {item.priority ? '★ PRIORIDAD' : item.status}
                      </span>
                    </p>
                    {item.locked && <p>🔒 No reagendable</p>}
                  </div>
                  <div className="popup-actions">
                    <button
                      className="btn-popup"
                      onClick={function() {
                        window.open(
                          'https://www.google.com/maps/dir/?api=1&destination=' + item.latitude + ',' + item.longitude,
                          '_blank'
                        );
                      }}
                    >
                      🗺️ Google Maps
                    </button>
                  </div>
                </div>
              </Popup>
              <Tooltip direction="top" offset={[0, -20]} opacity={0.9}>
                <strong>#{item.tspPosition}: {item.customerName || 'Pedido #' + item.purchaseId}</strong>
                {item.tspETA && <span> — {item.tspETA}</span>}
              </Tooltip>
            </Marker>
          );
        })}
      </Map>

      {/* Leyenda */}
      <div className="map-legend">
        <h4>Orden real de conducción</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#22c55e' }}></div>
            <span>Base (salida/retorno)</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#7c3aed' }}></div>
            <span>Prioridad ★</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#2563eb' }}></div>
            <span>Confirmado</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#f59e0b' }}></div>
            <span>Pendiente</span>
          </div>
          <div className="legend-item" style={{ gap: 6 }}>
            <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 4"/></svg>
            <span>Retorno a base</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueuePreviewMap;
